import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './style.scss';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

export interface UQTooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  position?: TooltipPosition;
  disabled?: boolean;
  delay?: number;
  className?: string;
  maxWidth?: number;
  usePortal?: boolean; // New prop to enable portal rendering
  maxHeight?: number; // Maximum height before showing scrollbar
  hideDelay?: number; // Delay before hiding tooltip (for easier interaction)
  interactive?: boolean; // Whether tooltip should stay visible when hovered
}

export const UQTooltip: React.FC<UQTooltipProps> = ({
  children,
  content,
  position = 'top',
  disabled = false,
  delay = 500,
  className = '',
  maxWidth = 250,
  usePortal = false, // Default to false for backward compatibility
  maxHeight = 300, // Default max height
  hideDelay = 300, // Default hide delay for easier interaction
  interactive = true, // Default to interactive for better UX
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [actualPosition, setActualPosition] = useState<TooltipPosition>(position);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const showTimeoutRef = useRef<number>();
  const hideTimeoutRef = useRef<number>();

  const clearAllTimeouts = () => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = undefined;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = undefined;
    }
  };

  const showTooltip = () => {
    if (disabled || !content) return;
    
    clearAllTimeouts();
    
    showTimeoutRef.current = setTimeout(() => {
      setIsVisible(true);
      calculatePosition();
    }, delay);
  };

  const hideTooltip = () => {
    clearAllTimeouts();
    
    // Only hide if not interactive, or if interactive, use a longer delay
    const actualHideDelay = interactive ? hideDelay : 0;
    
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, actualHideDelay);
  };

  const hideTooltipImmediately = () => {
    clearAllTimeouts();
    setIsVisible(false);
  };

  const handleTooltipMouseEnter = () => {
    if (interactive) {
      clearAllTimeouts();
      // Keep tooltip visible
      if (!isVisible) {
        setIsVisible(true);
        calculatePosition();
      }
    }
  };

  const handleTooltipMouseLeave = () => {
    if (interactive) {
      hideTooltip();
    }
  };

  const handleTriggerMouseEnter = () => {
    showTooltip();
  };

  const handleTriggerMouseLeave = () => {
    if (interactive) {
      // For interactive tooltips, use a shorter delay to allow moving to tooltip
      clearAllTimeouts();
      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, 150); // Shorter delay for moving to tooltip
    } else {
      hideTooltip();
    }
  };

  const calculatePosition = () => {
    if (!tooltipRef.current || !triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    let newPosition = position;

    switch (position) {
      case 'top':
        if (triggerRect.top - tooltipRect.height < 10) {
          newPosition = 'bottom';
        }
        break;
      case 'bottom':
        if (triggerRect.bottom + tooltipRect.height > viewport.height - 10) {
          newPosition = 'top';
        }
        break;
      case 'left':
        if (triggerRect.left - tooltipRect.width < 10) {
          newPosition = 'right';
        }
        break;
      case 'right':
        if (triggerRect.right + tooltipRect.width > viewport.width - 10) {
          newPosition = 'left';
        }
        break;
    }

    setActualPosition(newPosition);

    if (usePortal && tooltipRef.current) {
      const tooltip = tooltipRef.current;
      let top = 0;
      let left = 0;

      switch (newPosition) {
        case 'top':
          top = triggerRect.top - tooltipRect.height - 8;
          left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
          break;
        case 'bottom':
          top = triggerRect.bottom + 8;
          left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
          break;
        case 'left':
          top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
          left = triggerRect.left - tooltipRect.width - 8;
          break;
        case 'right':
          top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
          left = triggerRect.right + 8;
          break;
      }

      left = Math.max(10, Math.min(left, viewport.width - tooltipRect.width - 10));
      top = Math.max(10, Math.min(top, viewport.height - tooltipRect.height - 10));

      tooltip.style.position = 'fixed';
      tooltip.style.top = `${top}px`;
      tooltip.style.left = `${left}px`;
      tooltip.style.transform = 'none';
    }
  };

  useEffect(() => {
    if (isVisible) {
      calculatePosition();
    }
  }, [isVisible]);

  useEffect(() => {
    return () => {
      clearAllTimeouts();
    };
  }, []);

  const renderTooltip = () => {
    if (!isVisible || !content) return null;

    const tooltipStyle: React.CSSProperties = {
      maxWidth: `${maxWidth}px`,
      maxHeight: `${maxHeight}px`,
    };

    const tooltipElement = (
      <div
        ref={tooltipRef}
        className={`uq-tooltip uq-tooltip--${actualPosition} ${usePortal ? 'uq-tooltip--portal' : ''} ${interactive ? 'uq-tooltip--interactive' : ''}`}
        style={tooltipStyle}
        role="tooltip"
        onMouseEnter={interactive ? handleTooltipMouseEnter : undefined}
        onMouseLeave={interactive ? handleTooltipMouseLeave : undefined}
      >
        <div className={`uq-tooltip-content ${maxHeight ? 'uq-tooltip-content--scrollable' : ''}`}>{content}</div>
        <div className="uq-tooltip-arrow" />
      </div>
    );

    return usePortal ? ReactDOM.createPortal(tooltipElement, document.body) : tooltipElement;
  };

  return (
    <div className={`uq-tooltip-wrapper ${className}`}>
      <div
        ref={triggerRef}
        className="uq-tooltip-trigger"
        onMouseEnter={handleTriggerMouseEnter}
        onMouseLeave={handleTriggerMouseLeave}
        onFocus={showTooltip}
        onBlur={hideTooltipImmediately}
      >
        {children}
      </div>
      {renderTooltip()}
    </div>
  );
};

export default UQTooltip;
